import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as Styles from './StudioAdBanner.styles';
import { useResize } from '@/hooks/useResize';
import ModalFeaturePro from '@/pages/Studio/components/ModalFeaturePro';
import { ID_PRO_FEATURES } from '@/pages/Studio/studio.enum';
import { useDispatch, useSelector } from 'react-redux';
import { preChatActions, studioActions } from '@/redux/modules';
import { useStudio } from '@/hooks/studio';
import { useHistory } from 'react-router';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { StudioTopBanner } from '../StudioTopBanner';
import { StudioBottomBanner } from '../StudioBottomBanner';
import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import LoaderPage from '@/pages/Loader/LoaderPage';
import { createPortal } from 'react-dom';

const StudioAdBanner = ({ position, clickAdvertising, clickAdvertisingBottom }) => {
  const history = useHistory();
  const [loadingProgress, setLoadingProgress] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;
  const { provisioningFreePlan, provisioningTrailWithoutCreditCard } = useStudio();

  const { routes: routesLocale, studio: studioLocale } = useLocale();
  const bannerAdLocale = studioLocale.advertisingBanner;
  const { productsList } = useSelector(state => state.preChat);
  const {
    product: studioAccountProduct, products: studioProducts, provisionFree, loading: studioAccountLoading,
  } = useSelector(state => state.studio);

  const activeDomains = useMemo(() => (
    (productsList.domains && productsList.domains.filter(
      product => product && product.status && product.status.toLowerCase() === 'active',
    )) || []
  ), [productsList.domains]);

  const activeHostings = useMemo(() => (
    (productsList.hostings && productsList.hostings.filter(
      product => product && product.status && product.status.toLowerCase() === 'active',
    )) || []
  ), [productsList.hostings]);

  const activeStudio = useMemo(() => (
    (productsList.hostings && productsList.hostings.find(
      product => product && product.status && product.package_id
          && product.status.toLowerCase() === 'active' && HgProducts.STUDIO_ALL.includes(product.package_id),
    ))
  ), [productsList.hostings]);

  const dispatch = useDispatch();

  const hostingsWithoutStudios = activeHostings && activeHostings.filter(product => !HgProducts.STUDIO_ALL.includes(product.package_id));
  const hasHostingsOrDomains = (hostingsWithoutStudios && hostingsWithoutStudios.length > 0) || (activeDomains && activeDomains.length > 0);

  const getHomeRequest = useCallback(() => {
    dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: true, waitToken: true }));
    dispatch(studioActions.products.request());
    dispatch(studioActions.account.request());
  }, [dispatch]);

  useEffect(() => {
    getHomeRequest();
  }, [getHomeRequest]);

  const provisionFreePlan = useCallback(() => {
    const trialFlow = studioProducts && studioProducts.data.has_discount;

    if (!activeStudio) {
      if (trialFlow) {
        provisioningFreePlan(); return;
      }

      const onSuccessCallback = () => {
        history.push(routesLocale.studio);
      };

      setShowLoading(true);
      provisioningFreePlan(onSuccessCallback);
    }
  }, [studioProducts, history, routesLocale, provisioningFreePlan, activeStudio]);

  const handleShowModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const loadingProgressFunc = useCallback(() => {
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 85) {
          clearInterval(interval);
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadingProgressFunc();
  }, [loadingProgressFunc]);

  const bannerProps = useCallback(() => ({
    bannerAdLocale,
    provisionFreePlan,
    handleShowModal,
    trialAvailable: !!hasHostingsOrDomains,
    clickAdvertising,
    clickAdvertisingBottom,
    mobile,
  }), [bannerAdLocale, provisionFreePlan, handleShowModal, hasHostingsOrDomains, clickAdvertising, clickAdvertisingBottom, mobile]);

  const modalFeatureProProps = useCallback(() => {
    const productsData = studioProducts && studioProducts.data;

    const purchaseTrialWithoutCc = () => {
      const onSuccessCallback = () => {
        setShowModal(false);
        history.push(routesLocale.studio, { redirectFrom: routesLocale.fastcheckout });
      };

      provisioningTrailWithoutCreditCard(onSuccessCallback);
    };

    return {
      setModalFeatureProToShow: setShowModal,
      modalId: ID_PRO_FEATURES.STUDIO_PRO_OFFER,
      trialAvailable: !!hasHostingsOrDomains,
      hasDiscount: productsData && productsData.has_discount,
      productTrial: productsData && productsData.studio_trial,
      productPro: productsData && productsData.studio_pro,
      discount: productsData && productsData.studio_discount,
      accountProduct: studioAccountProduct,
      provisionFreePlan,
      purchaseTrialWithoutCc,
      loading: provisionFree.loading || studioAccountLoading,
    };
  }, [
    hasHostingsOrDomains,
    studioProducts,
    history,
    studioAccountProduct,
    provisionFreePlan,
    provisioningTrailWithoutCreditCard,
    provisionFree.loading,
    studioAccountLoading,
    routesLocale.studio,
    routesLocale.fastcheckout,
  ]);

  if (showLoading) {
    return createPortal(
      <Styles.WrapperLoaderPage>
        <LoaderPage progress={loadingProgress} />
      </Styles.WrapperLoaderPage>,
      document.body,
    );
  }

  if (showModal) {
    return (
      <ModalFeaturePro
        {...modalFeatureProProps()}
      />
    );
  }

  if (!activeStudio) {
    return (
      <>
        {position === 'top' && (
          <StudioTopBanner {...bannerProps()} />
        )}

        {position === 'bottom' && (
          <StudioBottomBanner {...bannerProps()} />
        )}
      </>
    );
  }

  return null;
};

export default withErrorBoundary(StudioAdBanner, 'StudioAdBanner');
